import { ChevronDown, Close } from '@carbon/react/icons';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';

import useClickOutside from 'lib/hooks/useClickOutside';
import styles from './free-text-combobox.module.scss';

type FreeTextComboBoxItem = Record<string, any>;

export interface FreeTextComboBoxProps {
  /** Unique identifier for the input */
  id: string;
  /** Label displayed above the input */
  titleText?: string;
  /** Array of selectable items */
  items: FreeTextComboBoxItem[];
  /** Currently selected item (object from items array, or synthetic { value, label }) */
  selectedItem?: FreeTextComboBoxItem | null;
  /** Converts an item to its display string */
  itemToString?: (item: FreeTextComboBoxItem | null) => string;
  /**
   * Extra text a typed query may also match, beyond the item's display string (e.g. a development
   * area's aliases). The list comes back from a server search that already matched on it, so the
   * client-side narrowing has to match on it too or it would drop those results.
   */
  itemToSearchText?: (item: FreeTextComboBoxItem) => string;
  /** Optional custom renderer for menu items. Replaces the default option div. */
  itemToElement?: (item: FreeTextComboBoxItem) => React.ReactNode;
  /** Called when an item is selected or free text is committed */
  onChange: (event: { selectedItem: FreeTextComboBoxItem | null }) => void;
  /** Called on every input keystroke with the current text */
  onInputChange?: (text: string) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Additional CSS class name */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * A ComboBox that accepts both list selections and arbitrary free text.
 * Visually matches Carbon's ComboBox by replicating the exact DOM nesting
 * and BEM class names that Carbon generates internally.
 * Does not use Downshift — manages open/close, filtering, and keyboard
 * navigation with plain React state.
 * @param {FreeTextComboBoxProps} props - Component props
 * @return {JSX.Element} A ComboBox-like input with free text support
 */
function FreeTextComboBox({
  id,
  titleText,
  items,
  selectedItem,
  itemToString = (item) => (item as { label?: string })?.label ?? '',
  itemToElement,
  itemToSearchText,
  onChange,
  onInputChange,
  placeholder,
  className,
  disabled = false,
}: FreeTextComboBoxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedText = selectedItem ? itemToString(selectedItem) : '';
  const [inputValue, setInputValue] = useState(selectedText);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Sync input when selectedItem changes externally. Compare by display
  // text rather than reference — the parent may pass a new object with the
  // same value when its items array recalculates (e.g. after a typeahead
  // fetch), and a reference check would incorrectly reset the input.
  const prevSelectedTextRef = useRef(selectedText);
  useEffect(() => {
    if (selectedText !== prevSelectedTextRef.current) {
      setInputValue(selectedText);
    }
    prevSelectedTextRef.current = selectedText;
  }, [selectedText]);

  // Filter items by input value (case-insensitive). When the input still
  // shows the selected value (user hasn't typed anything new), show all
  // items so the full option list is visible on dropdown open. When the
  // user's typed text doesn't exactly match any filtered item, prepend it
  // as a selectable option so the user can commit their own casing.
  const filteredItems = useMemo(() => {
    if (!inputValue || inputValue === selectedText) return items;
    const lower = inputValue.toLowerCase();
    // Token-based match: every whitespace-separated word of the query must
    // appear somewhere in the item label, rather than as one contiguous
    // substring. Aligns with the server's token-AND search so results whose
    // words aren't adjacent in the label (e.g. "delaware tier 2" vs
    // "DELAWARE BASIN TIERS - Tier 2") aren't dropped client-side. (INS-10852)
    const tokens = lower.split(/\s+/).filter(Boolean);
    const matched = items.filter((item) => {
      const searchable =
        `${itemToString(item)} ${itemToSearchText?.(item) ?? ''}`.toLowerCase();
      return tokens.every((token) => searchable.includes(token));
    });
    const trimmed = inputValue.trim();
    // The typed text is offered as a row only when some item matched it by its label. An item that matched
    // only through `itemToSearchText` (a tag, say) is a hit on text the user can't see as a name, so
    // offering that text as a new value above it would let a keystroke write it as the unit's name.
    const matchedByLabel = items.some((item) => {
      const label = itemToString(item).toLowerCase();
      return tokens.every((token) => label.includes(token));
    });
    if (
      trimmed &&
      matchedByLabel &&
      !matched.some((item) => itemToString(item) === trimmed)
    ) {
      return [{ value: trimmed, label: trimmed }, ...matched];
    }
    return matched;
  }, [items, inputValue, itemToString, itemToSearchText, selectedText]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !menuRef.current) return;
    const item = menuRef.current.children[highlightedIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  /**
   * Commit the current input value. If it exactly matches a list item,
   * select that item. Otherwise create a synthetic item for the free text
   * (preserving the user's casing).
   */
  const commitValue = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      onChange({ selectedItem: null });
      return;
    }
    const exactMatch = items.find((item) => itemToString(item) === trimmed);
    if (exactMatch) {
      onChange({ selectedItem: exactMatch });
    } else {
      onChange({ selectedItem: { value: trimmed, label: trimmed } });
    }
  }, [inputValue, items, itemToString, onChange]);

  // Close on outside click
  const handleClickOutside = useCallback(() => {
    if (isOpen) {
      commitValue();
      setIsOpen(false);
    }
  }, [isOpen, commitValue]);

  useClickOutside({ refs: [containerRef], onClickOutside: handleClickOutside });

  /**
   * Select an item from the dropdown list.
   * @param {FreeTextComboBoxItem} item - The selected item
   */
  const selectItem = useCallback(
    (item: FreeTextComboBoxItem) => {
      const text = itemToString(item);
      setInputValue(text);
      setIsOpen(false);
      setHighlightedIndex(-1);
      // Defer onChange so React flushes the queued setInputValue before
      // the parent's form.setFieldValue triggers a synchronous re-render
      // via useSyncExternalStore.
      Promise.resolve().then(() => onChange({ selectedItem: item }));
    },
    [itemToString, onChange]
  );

  /**
   * Handle input text changes.
   * @param {string} text - The new input value
   */
  const handleInputChange = useCallback(
    (text: string) => {
      setInputValue(text);
      setIsOpen(true);
      setHighlightedIndex(-1);
      onInputChange?.(text);
    },
    [onInputChange]
  );

  /**
   * Handle keyboard navigation within the dropdown.
   * @param {KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) => {
              return prev < filteredItems.length - 1 ? prev + 1 : 0;
            });
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) => {
              return prev > 0 ? prev - 1 : filteredItems.length - 1;
            });
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            selectItem(filteredItems[highlightedIndex]);
          } else {
            commitValue();
            setIsOpen(false);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    },
    [isOpen, highlightedIndex, filteredItems, selectItem, commitValue]
  );

  /** Clear the input and selection */
  const handleClear = useCallback(() => {
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange({ selectedItem: null });
    onInputChange?.('');
    inputRef.current?.focus();
  }, [onChange, onInputChange]);

  /**
   * Toggle the dropdown menu open/closed.
   */
  const handleMenuToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    inputRef.current?.focus();
  }, [disabled]);

  return (
    <div
      ref={containerRef}
      className={`bx--list-box__wrapper ${className ?? ''}`}
    >
      {titleText && (
        <label
          htmlFor={id}
          className={`bx--label${disabled ? ' bx--label--disabled' : ''}`}
        >
          {titleText}
        </label>
      )}
      <div
        className={`bx--combo-box bx--list-box${
          disabled ? ' bx--list-box--disabled' : ''
        }${isOpen ? ' bx--list-box--expanded' : ''}`}
      >
        <div className="bx--list-box__field">
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            type="text"
            className={`bx--text-input${
              !inputValue ? ' bx--text-input--empty' : ''
            }`}
            value={inputValue}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-controls={`${id}-menu`}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${id}-item-${highlightedIndex}`
                : undefined
            }
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
          />
          {inputValue && !disabled && (
            <button
              type="button"
              className={`bx--list-box__selection ${styles.clearButton}`}
              onClick={handleClear}
              aria-label="Clear selection"
              tabIndex={-1}
            >
              <Close />
            </button>
          )}
          <button
            type="button"
            className={`bx--list-box__menu-icon${
              isOpen ? ' bx--list-box__menu-icon--open' : ''
            }`}
            onClick={handleMenuToggle}
            tabIndex={-1}
            aria-label="Open menu"
          >
            <ChevronDown />
          </button>
        </div>
        {isOpen && filteredItems.length > 0 && (
          <div
            ref={menuRef}
            id={`${id}-menu`}
            className="bx--list-box__menu"
            role="listbox"
          >
            {filteredItems.map((item, index) => (
              <div
                key={itemToString(item)}
                id={`${id}-item-${index}`}
                role="option"
                aria-selected={highlightedIndex === index}
                className={`bx--list-box__menu-item${
                  highlightedIndex === index
                    ? ' bx--list-box__menu-item--highlighted'
                    : ''
                }`}
                style={itemToElement ? { height: 'auto' } : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectItem(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {itemToElement ? (
                  itemToElement(item)
                ) : (
                  <div className="bx--list-box__menu-item__option">
                    {itemToString(item)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { FreeTextComboBox };
export type { FreeTextComboBoxItem };
